import { FlagOutlined, PlusCircleOutlined, SaveOutlined } from '@ant-design/icons';
import { Button, Checkbox, Col, Form, Input, InputNumber, message, Modal, Row, Select } from 'antd';
import React, { useState } from 'react';
import Helmet from 'react-helmet';
import ZTable, { IFilterItem, ITableRef, IColumnType } from '@/components/ZTable';
import MainContent from '@/layouts/MainContent';
import { useTranslation } from 'react-i18next';
import { ModalWidth, TimeZoneOptions, Timezones, VALIDATE_REQUIRED } from '@/config';
import { clearAllCountriesCache } from '@/hooks/data/masterDataHook';
import BooleanTag from '@/components/app/BooleanTag';
import { CountryModel } from '@/__generated';
import { apiSdk } from '@/httpClient';
const PAGE_TITLE = 'countryPage_countries';

const columns: IColumnType<CountryModel>[] = [
  {
    title: 'countryPage_countryName',
    dataIndex: 'countryName',
    sorter: true,
    defaultSortOrder: 'ascend',
  },
  {
    title: 'countryPage_noOfGsmOperators',
    dataIndex: 'noOfGsmOperators',
    sorter: true,
  },
  {
    title: 'countryPage_nationalNumLength',
    dataIndex: 'nationalNumLength',
    sorter: true,
  },
  {
    title: 'countryPage_internationalCountryCode',
    dataIndex: 'internationalCountryCode',
    sorter: true,
  },
  {
    title: 'countryPage_alphaCode',
    dataIndex: 'alphaCode',
    sorter: true,
  },
  {
    title: 'countryPage_UpdateCountry_IsOperating',
    dataIndex: 'isOperating',
    sorter: true,
    render: v => <BooleanTag value={v} />,
  },
  {
    title: 'countryPage_timeZoneId',
    dataIndex: 'timeZone',
    sorter: true,
  },
];

const filters: Array<IFilterItem> = [{ type: 'TEXT', name: 'countryName', label: 'countryPage_countryName' }];

const Countries = () => {
  const [t] = useTranslation();
  const zTb = React.useRef<ITableRef>();
  const [form] = Form.useForm();
  const [formVisible, setFormVisible] = useState(false);
  const [isNewForm, setIsNewForm] = useState(false);
  const [loading, setLoading] = useState(false);
  const clearCache = () => {
    clearAllCountriesCache();
  };
  const createCountry = values => {
    setLoading(true);
    apiSdk.CountryService.create({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const updateCountry = values => {
    setLoading(true);
    apiSdk.CountryService.update({ body: values })
      .then(response => {
        if (response?.success) {
          setFormVisible(false);
          zTb.current?.reload();
          clearCache();
        } else {
          if (response.message) {
            message.error(t(response.message || ''));
          }
        }
      })
      .catch(error => {
        message.error(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <MainContent
      title={t(PAGE_TITLE)}
      extraBtns={[
        <Button
          key="create"
          type="primary"
          className="nta-create-btn"
          icon={<PlusCircleOutlined />}
          onClick={() => {
            form.resetFields();
            setIsNewForm(true);
            setFormVisible(true);
          }}
        >
          {t('createNew')}
        </Button>,
      ]}
      icon={<FlagOutlined style={{ fontSize: 16 }} />}
    >
      <Helmet title={t(PAGE_TITLE)} />
      <div className="ant-layout-content-body">
        <ZTable
          columns={columns}
          primaryKey="id"
          tableRef={zTb}
          url="/commons/countries/get-countries"
          bordered
          order_by="countryName"
          order_by_asc={true}
          allowEdit={true}
          onEdit={record => {
            setIsNewForm(false);
            setFormVisible(true);
            form.setFieldsValue({
              ...record,
              continent: `${record.continent}`,
            });
          }}
          allowDelete={true}
          delete_url="/commons/countries/delete"
          filters={filters}
          onDeleteCompleted={clearCache}
        ></ZTable>
        <Modal
          visible={formVisible}
          maskClosable={false}
          width={ModalWidth.small}
          className="nta-custom-form"
          title={isNewForm ? t('countryPage_CreateCountry') : t('countryPage_UpdateCountry')}
          forceRender={true}
          okText={t('save')}
          cancelText={t('cancel')}
          okButtonProps={{ icon: <SaveOutlined />, className: 'save-btn', loading: loading }}
          onCancel={() => {
            form.resetFields();
            setFormVisible(false);
          }}
          onOk={() => {
            form
              .validateFields()
              .then(values => {
                if (values.id) {
                  updateCountry(values);
                } else {
                  createCountry(values);
                }
              })
              .catch(info => {});
          }}
        >
          <Form form={form} size="middle" layout="vertical">
            <Form.Item name="id" className="hidden">
              <Input type="hidden" />
            </Form.Item>
            <Row gutter={6}>
              <Col md={24}>
                <Form.Item label={t('countryPage_countryName')} name="countryName" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={6}>
              <Col md={12}>
                <Form.Item
                  label={t('countryPage_internationalCountryCode')}
                  name="internationalCountryCode"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item label={t('countryPage_alphaCode')} name="alphaCode">
                  <Input />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item label={t('countryPage_timeZoneId')} name="timeZone" rules={[{ required: true, message: VALIDATE_REQUIRED }]}>
                  <Select
                    options={TimeZoneOptions}
                    showSearch
                    filterOption={(input, option: any) => {
                      return (option?.label as any)?.toLowerCase().includes(input.toLowerCase());
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={6}>
              <Col md={12}>
                <Form.Item label={t('countryPage_noOfGsmOperators')} name="noOfGsmOperators">
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
              </Col>
              <Col md={12}>
                <Form.Item
                  label={t('countryPage_nationalNumLength')}
                  name="nationalNumLength"
                  rules={[{ required: true, message: VALIDATE_REQUIRED }]}
                >
                  <InputNumber min={0} className="w-100" />
                </Form.Item>
              </Col>
              <Col md={24}>
                <Form.Item valuePropName="checked" name="isOperating">
                  <Checkbox> {t('countryPage_UpdateCountry_IsOperating')}</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </Modal>
      </div>
    </MainContent>
  );
};
export default Countries;
